import { Autocomplete, Box, Button, Checkbox, FormHelperText, Grid, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { filter, find } from "lodash";
import { useEffect, useState } from "react";
import PatternsConfigurationOptions from "./UserDefinedFaultTable";
import { Plus } from "../../../icons/plus";
import MaterialReactTable from "material-react-table";
import { ANALYSIS_SCOPE_OPTIONS, SEVERITY_OPTIONS } from "../../../constants";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

interface IDataExposurePicker {
  label: string;
  patterns: any[];
  initialValue: any;
  helperText?: string;
  onChange: (selectedPatterns: any[], hasError: boolean) => void;
}

const DataExposurePicker: React.FC<IDataExposurePicker> = ({
  label,
  patterns,
  helperText = "",
  initialValue,
  onChange,
}) => {

  const [tableData, setTableData] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<number, Record<string, string>>>({});


  useEffect(() => {
    
    if(initialValue){
      const selectedValues = initialValue?.map((value: any) => {
      let selectedValue = patterns.find((pattern: any) => pattern.id === value.patternsId || pattern.id === value.id || pattern.id === value.patterns);
      return { ...selectedValue, faultId: value?.unauthenticatedFaultId, findingName: value?.findingName, severity: value?.severity, reportAuthenticated: value?.reportAuthenticated }
    })
    if (selectedValues) {
      setTableData([...selectedValues])
    }
  }
  }, []);

  const hasAnyError = (errors: Record<number, Record<string, string>>) => {
    return Object.values(errors).some((rowErrors) => Object.keys(rowErrors).length > 0);
  };

  const validateRow = (row: any) => {
    const errors: Record<string, string> = {};
  
    // Rule 1: Pattern is required
    if (!row?.id) {
      errors.pattern = "Pattern is required";
    }
  
    // Rule 2: Conditional group validation
    const faultIdPresent = !!row.faultId;
    const findingNamePresent = !!row.findingName;
    const severityPresent = !!row.severity;
  
    const anyGroupFieldPresent = faultIdPresent || findingNamePresent || severityPresent;
  
    if (anyGroupFieldPresent) {
      if (!faultIdPresent) errors.faultId = "Required";
      if (!findingNamePresent) errors.findingName = "Required";
      if (!severityPresent) errors.severity = "Required";
    }
  
    return errors;
  };
  

  const handleChange = (index: number, type: string, value: any) => {
    const newValues = [...tableData];
    if (type === 'pattern') {
      newValues[index] = { ...newValues[index], ...value };
    } else {
      newValues[index][type] = value;
    }

    const errors = validateRow(newValues[index]);
  
    setValidationErrors(prevErrors => {
      const updatedErrors = { ...prevErrors, [index]: errors };

      if (Object.keys(errors).length === 0) {
        delete updatedErrors[index];
      }
      const hasError = Object.values(updatedErrors).some(row => Object.keys(row).length > 0);
      onChange(newValues, hasError);
  
      return updatedErrors;
    });
  
    setTableData(newValues);
  };
  


  const handleAddRow = () => {
    const newRow: any = {
      id: "",
      name: "",
      faultId: "",
      findingName: "",
      reportAuthenticated: false,
      severity: ""
    };
  
    const updatedTable = [newRow, ...tableData];
  
    // Shift all existing validationErrors down by 1
    const shiftedErrors: Record<number, Record<string, string>> = {};
    Object.entries(validationErrors).forEach(([key, val]) => {
      shiftedErrors[Number(key) + 1] = val;
    });
  
    // Add the new error for the first row
    shiftedErrors[0] = { pattern: "Pattern is required" };
  
    setTableData(updatedTable);
    setValidationErrors(shiftedErrors);
    onChange(updatedTable, hasAnyError(shiftedErrors));
  };
  
  

  const handleDeleteRow = (index: number) => {
    const newValue = [...tableData];
    newValue.splice(index, 1);
  
    const reindexedErrors: Record<number, Record<string, string>> = {};
    Object.entries(validationErrors).forEach(([key, val]) => {
      const i = Number(key);
      if (i < index) {
        reindexedErrors[i] = val;
      } else if (i > index) {
        reindexedErrors[i - 1] = val;
      }
      
    });
  
    setTableData(newValue);
    setValidationErrors(reindexedErrors);
    onChange(newValue, hasAnyError(reindexedErrors));
  };
  
  

  return (
    <Box>
      <Grid container sx={{ mt: 2 }} columnSpacing={4} >
      <Grid item xs={12}>
            
              <Box sx={{pb:3, pl:1}}>
                <Typography variant='body2'>
                  By default, a finding is reported when a pattern is detected in a response to an unauthenticated request. Optionally, you can enable reporting for authenticated requests.  In addition, you may optionally assign a custom fault ID, name, and severity to the finding that is reported.
                </Typography>
            </Box>
          </Grid>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: "space-between" }}>
              <Box>
                <Button size="small" variant="outlined" sx={{ mb: 2, ml: 1, borderColor: 'neutral.300', color: 'text.primary' }} onClick={handleAddRow}>
                  Add Pattern <Plus />
                </Button>


              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex" }}>


            </Box>
          </Grid>

          <MaterialReactTable
            muiTablePaperProps={{
              elevation: 0,
              sx: { marginBottom: 0 }
            }}
            enableGlobalFilter={false}
            columns={[
              {
                accessorKey: "name",
                header: "Pattern",
                Cell: (params: any) => {
                  const currentRow = params.row.original;
                  const selectedPattern = patterns.find((p) => p.id === currentRow.id);
                  const rowIndex = params.row.index;
                  return (
                    <Autocomplete
                      options={patterns}
                      getOptionLabel={(option) => option.name || ""}
                      value={selectedPattern || null}
                      onChange={(e, value) => handleChange(params.row.index, "pattern", value)}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      size="small"
                      disableClearable
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Typography variant="body1">{option.name}</Typography>
                        </li>
                      )}
                      filterOptions={(options, params) =>
                        filter(options, (item) =>
                          item.name?.toLowerCase().includes(params.inputValue?.toLowerCase())
                        )
                      }
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label="Pattern"
                          variant="outlined"
                          error={!!validationErrors[rowIndex]?.pattern}
                          helperText={validationErrors[rowIndex]?.pattern}
                        />
                      )}
                    />
                  );
                },
                
                size: 250,
              },
              {
                accessorKey: "reportAuthenticated",
                header: "Analysis Scope",
                Cell: ({ row }: any) => (
                  <Select
                    value={row?.original?.reportAuthenticated ?? ""}
                    onChange={(e) =>
                      handleChange(row?.index, "reportAuthenticated", e?.target?.value)
                    }
                    
                    sx={{ width: "18em", textAlign: "left", flex: 1 }}
                    MenuProps={{
                      disablePortal: true,
                    }}
                  >
                    
                    {ANALYSIS_SCOPE_OPTIONS.map((type: any) => (
                      <MenuItem key={type.key} value={type.key}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                ),
                size: 300
              },
              {
                accessorKey: "faultId",
                header: "Fault Id",
                Cell: (params: any) => {
                  return (
                    <TextField
                      value={params?.row?.original?.faultId}
                      onChange={(e) =>
                        handleChange(params?.row?.index, "faultId", e?.target?.value)
                      }
                      error={!!validationErrors[params.row.index]?.faultId}
                      helperText={validationErrors[params.row.index]?.faultId}
                    />
                  )
                }
              },
              {
                accessorKey: "findingName",
                header: "Finding Name",
                Cell: (params: any) => {
                  return (
                    <TextField
                      value={params?.row?.original?.findingName}
                      onChange={(e) =>
                        handleChange(params?.row?.index, "findingName", e?.target?.value)
                      }
                      error={!!validationErrors[params.row.index]?.findingName}
                      helperText={validationErrors[params.row.index]?.findingName}
                    />
                  )
                }
              },
              {
                accessorKey: "severity",
                header: "Severity",
                Cell: (params: any) => {
                  return (<><Select
                    value={params?.row?.original?.severity ?? ""}
                    onChange={(e) =>
                      handleChange(params?.row?.index, "severity", e?.target?.value)
                    }
                    error={!!validationErrors[params.row.index]?.severity}
                    sx={{ minWidth: "10em", maxWidth: "100%", textAlign: "left", flex: 1 }}
                    MenuProps={{
                      disablePortal: true,
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {SEVERITY_OPTIONS.map((type) => (
                      <MenuItem key={type.key} value={type.key}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>
                    {validationErrors[params.row.index]?.severity}
                  </FormHelperText>
                  </>)
                }
              },
              {
                //@ts-ignore
                accessorKey: "actions",
                header: "",
                Cell: (params: any) => {
                  return (

                   <Box sx={{ display: "flex", justifyContent: "start", alignContent: "top" }}>
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => handleDeleteRow(params?.row?.index)} disabled={params?.row?.original?.isDeleted}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>

                  )
                },
                size: 80
              },




            ]}
            data={tableData}
            initialState={{
              density: "compact",
              columnPinning: { left: ['name'] }
            }}

            enableColumnActions={false}
            enableSorting={false}
            enableStickyHeader
            muiTableBodyCellProps={{
              align: 'justify',
            }}
            

            muiTableContainerProps={{
              sx: { height: '60vh', overflowY: 'auto' },
            }}
            enableTopToolbar={false}
            enableBottomToolbar={false}
            enablePagination={false}
            enableDensityToggle={false}
            enableColumnFilters={false}
          />


        </Grid>
      </Grid>



    </Box>
  );
};

export default DataExposurePicker;
